import { cn } from "@/lib/utils";
import { ui } from "@patient-watch/ui";
import { History } from "lucide-react";
import dayjs from "dayjs";
import { useState } from "react";
import {
    useFormValidationHistoryQuery,
    useScheduleValidationHistoryQuery,
    type ValidationHistoryEntryT,
} from "../api/selectValidationHistory";

interface Props {
    entityType: "form" | "schedule";
    entityId: string;
}

function outcomeBadgeClasses(isValid: boolean | null) {
    if (isValid === null) return "bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-slate-100";
    return isValid
        ? "bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200"
        : "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200";
}

function outcomeLabel(isValid: boolean | null) {
    if (isValid === null) return "Pending";
    return isValid ? "Approved" : "Rejected";
}

function HistoryRow({ entry }: { entry: ValidationHistoryEntryT }) {
    const signedOffByExternal = Boolean(entry.on_behalf_of_name);

    return (
        <div className="bg-white dark:bg-slate-900 p-3 border rounded-md">
            <div className="flex justify-between items-start gap-3">
                <div className="min-w-0">
                    {signedOffByExternal ? (
                        <>
                            <div className="font-medium truncate">{entry.on_behalf_of_name}</div>
                            {entry.on_behalf_of_organisation ? (
                                <div className="text-muted-foreground text-xs truncate">
                                    {entry.on_behalf_of_organisation}
                                </div>
                            ) : null}
                            <div className="mt-1 text-muted-foreground text-xs">
                                Recorded by {entry.validated_by_name || entry.validated_by_email || "an admin"}
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="font-medium truncate">{entry.validated_by_name || "Unnamed validator"}</div>
                            {entry.validated_by_email ? (
                                <div className="text-muted-foreground text-xs truncate">{entry.validated_by_email}</div>
                            ) : null}
                        </>
                    )}
                </div>
                <span
                    className={cn(
                        "inline-flex items-center px-2 py-0.5 rounded-full text-xs shrink-0",
                        outcomeBadgeClasses(entry.is_valid)
                    )}
                >
                    {outcomeLabel(entry.is_valid)}
                </span>
            </div>
            {!signedOffByExternal ? (
                <div className="mt-2 text-muted-foreground text-xs">
                    Invited {dayjs(entry.created_at).format("D MMM YYYY, HH:mm")}
                </div>
            ) : null}
            {entry.validated_at ? (
                <div className={cn("text-muted-foreground text-xs", signedOffByExternal ? "mt-2" : "mt-1")}>
                    Resolved {dayjs(entry.validated_at).format("D MMM YYYY, HH:mm")}
                </div>
            ) : null}
            {entry.is_valid === false && entry.reason ? (
                <div className="mt-2 pt-2 border-t">
                    <div className="text-muted-foreground text-xs">Reason</div>
                    <div className="text-sm">{entry.reason}</div>
                </div>
            ) : null}
        </div>
    );
}

const ValidationHistoryDialog = ({ entityType, entityId }: Props) => {
    const [open, setOpen] = useState(false);
    const formHistory = useFormValidationHistoryQuery(
        entityType === "form" ? entityId : "",
        open && entityType === "form"
    );
    const scheduleHistory = useScheduleValidationHistoryQuery(
        entityType === "schedule" ? entityId : "",
        open && entityType === "schedule"
    );
    const { data: history, isLoading } = entityType === "form" ? formHistory : scheduleHistory;

    return (
        <ui.Dialog open={open} onOpenChange={setOpen}>
            <ui.DialogTrigger asChild>
                <ui.Button
                    variant="ghost"
                    size="sm"
                    className="justify-start gap-2 px-2 py-1.5 pr-4 w-full h-auto whitespace-nowrap"
                >
                    <History className="w-4 h-4" />
                    View validation history
                </ui.Button>
            </ui.DialogTrigger>
            <ui.DialogContent>
                <ui.DialogHeader>
                    <ui.DialogTitle>Validation history</ui.DialogTitle>
                    <ui.DialogDescription>
                        Every validation this {entityType} has ever had, newest first.
                    </ui.DialogDescription>
                </ui.DialogHeader>
                {isLoading ? (
                    <p className="text-muted-foreground text-sm">Loading...</p>
                ) : !history || history.length === 0 ? (
                    <p className="text-muted-foreground text-sm">No validation history yet.</p>
                ) : (
                    <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                        {history.map((entry) => (
                            <HistoryRow key={entry.validation_id} entry={entry} />
                        ))}
                    </div>
                )}
            </ui.DialogContent>
        </ui.Dialog>
    );
};

export default ValidationHistoryDialog;
